import { useEffect, useRef, useState } from 'react';
import { X, Upload, Trash2, FileDown, Search, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { exportUsualMealPdf, resolveFoodMediaUrl, UsualMealLike, formatItemMacroLine, formatMacroTotals } from '../utils/usualMealPdf';
import { applyMasterToItem, defaultPortionQty, portionFromMaster, portionInputLabel } from '../utils/foodPortionLabel';

const MEAL_TYPES = [
  { key: 'BREAKFAST', label: 'Desayuno' },
  { key: 'LUNCH', label: 'Almuerzo' },
  { key: 'DINNER', label: 'Cena' },
  { key: 'SNACK', label: 'Snack' },
  { key: 'CUSTOM', label: 'Otra' },
];

function roundMacro(n: number) {
  return Math.round(n * 10) / 10;
}

async function foodForUsualItem(it: any) {
  const id = it?.foodItemId || it?.food_item_id;
  if (id) {
    try {
      const { data } = await api.get(`/nutrition/foods/${id}`);
      if (data?.id) return data;
    } catch { /* buscar por nombre */ }
  }
  const name = String(it?.name || '').trim();
  if (name.length < 2) return null;
  try {
    const { data } = await api.get('/nutrition/foods/search', { params: { q: name, limit: 20 } });
    const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
    const exact = list.filter((f: any) => String(f?.name || '').trim().toLowerCase() === name.toLowerCase());
    return exact.length === 1 ? exact[0] : null;
  } catch {
    return null;
  }
}

function itemTotals(items: any[]) {
  return {
    totalCalories: roundMacro(items.reduce((a, it) => a + Number(it.calories || 0), 0)),
    totalProtein: roundMacro(items.reduce((a, it) => a + Number(it.protein || 0), 0)),
    totalCarbs: roundMacro(items.reduce((a, it) => a + Number(it.carbs || 0), 0)),
    totalFat: roundMacro(items.reduce((a, it) => a + Number(it.fat || 0), 0)),
  };
}

type Props = {
  open: boolean;
  initial?: UsualMealLike | null;
  /** Si se define, usa rutas coach `/trainer/students/:studentId/usual-meals` */
  studentId?: string | null;
  onClose: () => void;
  onSaved: (meal: any) => void;
};

export default function UsualMealEditorModal({
  open, initial, studentId, onClose, onSaved,
}: Props) {
  const [name, setName] = useState('');
  const [mealType, setMealType] = useState('LUNCH');
  const [ingredientsText, setIngredientsText] = useState('');
  const [cookTimeMinutes, setCookTimeMinutes] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [mealId, setMealId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [foodSearch, setFoodSearch] = useState('');
  const [foodResults, setFoodResults] = useState<any[]>([]);
  const [pickFood, setPickFood] = useState<any>(null);
  const [pickQty, setPickQty] = useState('100');
  /** Evita POSTs duplicados si se eligen varias fotos antes de hidratar mealId. */
  const createMealLockRef = useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || '');
    setMealType(initial?.mealType || 'LUNCH');
    setIngredientsText(initial?.ingredientsText || '');
    setCookTimeMinutes(
      initial?.cookTimeMinutes != null && initial.cookTimeMinutes !== undefined
        ? String(initial.cookTimeMinutes)
        : '',
    );
    setNotes(initial?.notes || '');
    const rawItems = Array.isArray(initial?.items) ? initial!.items! : [];
    setItems(rawItems);
    setPhotoUrls(Array.isArray(initial?.photoUrls) ? [...initial!.photoUrls!] : []);
    setMealId(initial?.id ? String(initial.id) : null);
    setFoodSearch('');
    setFoodResults([]);
    setPickFood(null);
    setPickQty('100');
    createMealLockRef.current = null;
    let cancelled = false;
    (async () => {
      const next = await Promise.all(rawItems.map(async (it) => {
        const food = await foodForUsualItem(it);
        return food ? applyMasterToItem(it, food) : it;
      }));
      if (!cancelled) {
        setItems((current) => {
          if (current.length !== rawItems.length) return current;
          const untouched = current.every((it, i) => (
            (it?.foodItemId || it?.food_item_id) === (rawItems[i]?.foodItemId || rawItems[i]?.food_item_id)
            && Number(it?.quantityGrams) === Number(rawItems[i]?.quantityGrams)
          ));
          return untouched ? next : current;
        });
      }
    })();
    return () => { cancelled = true; };
  }, [open, initial]);

  useEffect(() => {
    if (!open || foodSearch.length < 2) {
      setFoodResults([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const { data } = await api.get('/nutrition/foods/search', {
          params: { q: foodSearch, limit: 40 },
        });
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setFoodResults(list);
      } catch {
        if (!cancelled) setFoodResults([]);
      }
    }, 300);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [open, foodSearch]);

  if (!open) return null;

  const totals = itemTotals(items);

  const addFoodToItems = () => {
    if (!pickFood) return;
    const qty = pickQty.trim();
    if (!qty || Number(qty) <= 0) {
      toast.error('Indica una cantidad válida');
      return;
    }
    const portion = portionFromMaster(pickFood, Number(qty));
    setItems((prev) => [...prev, {
      foodItemId: pickFood.id,
      name: pickFood.name,
      ...portion,
    }]);
    setPickFood(null);
    setFoodSearch('');
    setFoodResults([]);
    setPickQty(defaultPortionQty(pickFood?.unit));
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const basePath = studentId
    ? `/trainer/students/${studentId}/usual-meals`
    : '/nutrition/usual-meals';

  const payload = () => ({
    name: name.trim(),
    mealType,
    ingredientsText,
    cookTimeMinutes: cookTimeMinutes === '' ? null : Number(cookTimeMinutes),
    notes,
    items,
    totalCalories: totals.totalCalories,
    totalProtein: totals.totalProtein,
    totalCarbs: totals.totalCarbs,
    totalFat: totals.totalFat,
    photoUrls,
  });

  /** Crea la comida si aún no existe (p. ej. al subir foto en modal «Nueva»). */
  const ensureMealId = async (): Promise<string | null> => {
    if (mealId) return mealId;
    if (createMealLockRef.current) return createMealLockRef.current;
    if (!name.trim()) {
      toast.error('Pon un nombre antes de subir fotos');
      return null;
    }
    createMealLockRef.current = (async () => {
      try {
        const r = await api.post(basePath, payload());
        const id = r.data?.id ? String(r.data.id) : null;
        if (!id) {
          toast.error('No se pudo crear la comida para subir la foto');
          createMealLockRef.current = null;
          return null;
        }
        setMealId(id);
        onSaved(r.data);
        return id;
      } catch (e: any) {
        createMealLockRef.current = null;
        const msg = e?.response?.data?.message;
        toast.error(Array.isArray(msg) ? msg.join(', ') : (msg || 'No se pudo crear la comida'));
        return null;
      }
    })();
    return createMealLockRef.current;
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error('Nombre requerido');
      return;
    }
    setSaving(true);
    try {
      // Si hay create en vuelo (foto), reutilizar ese id.
      let id = mealId;
      if (!id && createMealLockRef.current) {
        id = await createMealLockRef.current;
      }
      let meal: any;
      if (id) {
        const r = await api.patch(`${basePath}/${id}`, payload());
        meal = r.data;
      } else {
        const r = await api.post(basePath, payload());
        meal = r.data;
        if (meal?.id) {
          setMealId(String(meal.id));
          createMealLockRef.current = Promise.resolve(String(meal.id));
        }
      }
      toast.success(id || initial?.id ? 'Comida habitual actualizada' : 'Comida habitual creada');
      onSaved(meal);
      onClose();
    } catch (e: any) {
      toast.error(Array.isArray(e?.response?.data?.message) ? e.response.data.message.join(', ') : (e?.response?.data?.message || 'No se pudo guardar'));
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file: File) => {
    setUploading(true);
    try {
      const id = await ensureMealId();
      if (!id) return;
      // No forzar Content-Type: el browser debe enviar boundary multipart.
      const fd = new FormData();
      fd.append('photo', file);
      const r = await api.post(`${basePath}/${id}/photos`, fd);
      setPhotoUrls(r.data?.photoUrls || []);
      onSaved(r.data);
      toast.success('Foto subida');
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg.join(', ') : (msg || 'Error subiendo foto'));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (photoUrl: string) => {
    if (!mealId) return;
    try {
      const r = await api.delete(`${basePath}/${mealId}/photos`, { data: { photoUrl } });
      setPhotoUrls(r.data?.photoUrls || []);
      onSaved(r.data);
    } catch {
      toast.error('No se pudo quitar la foto');
    }
  };

  const exportPdf = async () => {
    const ok = await exportUsualMealPdf({
      name, mealType, ingredientsText, notes, items, photoUrls,
      cookTimeMinutes: cookTimeMinutes === '' ? null : Number(cookTimeMinutes),
      totalCalories: totals.totalCalories,
      totalProtein: totals.totalProtein,
      totalCarbs: totals.totalCarbs,
      totalFat: totals.totalFat,
    });
    if (!ok) toast.error('No se pudo abrir el PDF. Revisa el bloqueo de ventanas emergentes.');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-5 max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-800">
            {mealId ? 'Editar comida habitual' : 'Nueva comida habitual'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-3">
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Nombre</span>
            <input className="input mt-1 w-full" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Tipo</span>
            <select className="input mt-1 w-full" value={mealType} onChange={(e) => setMealType(e.target.value)}>
              {MEAL_TYPES.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Ingredientes / preparación</span>
            <textarea
              className="input mt-1 w-full min-h-[90px]"
              value={ingredientsText}
              onChange={(e) => setIngredientsText(e.target.value)}
              placeholder="Lista de ingredientes y pasos breves…"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Tiempo de cocción (minutos)</span>
            <input
              className="input mt-1 w-full"
              type="number"
              min={0}
              value={cookTimeMinutes}
              onChange={(e) => setCookTimeMinutes(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Notas</span>
            <textarea
              className="input mt-1 w-full min-h-[60px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <div className="border border-indigo-100 rounded-xl p-3 bg-indigo-50/40 space-y-2">
            <p className="text-sm font-semibold text-gray-800">Alimentos del catálogo</p>
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-400" aria-hidden />
              <input
                className="input input-with-leading-icon w-full text-sm"
                placeholder="Buscar alimento (ej. huevo, banano)…"
                value={foodSearch}
                onChange={(e) => {
                  setFoodSearch(e.target.value);
                  setPickFood(null);
                }}
                aria-label="Buscar alimento del catálogo"
              />
            </div>
            {foodResults.length > 0 && !pickFood && (
              <ul className="max-h-36 overflow-y-auto border border-gray-100 rounded-lg bg-white divide-y divide-gray-100">
                {foodResults.map((f) => (
                  <li key={f.id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50"
                      onClick={() => {
                        setPickFood(f);
                        setFoodSearch(f.name);
                        setFoodResults([]);
                        setPickQty(defaultPortionQty(f.unit));
                      }}
                    >
                      <span className="font-medium text-gray-800">{f.name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {Math.round(Number(f.caloriesPer100g ?? f.calories_per_100g ?? 0))} kcal/100g
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {pickFood && (
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-xs flex-1 min-w-[120px]">
                  <span className="text-gray-600">
                    Cantidad ({portionInputLabel(pickFood.unit)})
                  </span>
                  <input
                    className="input mt-1 w-full text-sm"
                    type="number"
                    min={0}
                    step="any"
                    value={pickQty}
                    onChange={(e) => setPickQty(e.target.value)}
                  />
                </label>
                <button type="button" className="btn-primary text-xs inline-flex items-center gap-1" onClick={addFoodToItems}>
                  <Plus size={14} /> Agregar
                </button>
              </div>
            )}
          </div>

          {items.length > 0 && (
            <div className="text-xs text-gray-500 bg-gray-50 rounded-xl p-3">
              <p className="font-semibold text-gray-700 mb-1">
                Alimentos nutricionales ({items.length}) · {formatMacroTotals(totals)}
              </p>
              <ul className="space-y-1">
                {items.map((it, i) => (
                  <li key={`${it.foodItemId || it.name}-${i}`} className="flex items-center justify-between gap-2">
                    <span>{formatItemMacroLine(it)}</span>
                    <button type="button" className="text-red-500 hover:text-red-700 p-0.5" onClick={() => removeItem(i)} aria-label="Quitar">
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Fotos</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {photoUrls.map((u) => (
                <div key={u} className="relative">
                  <img src={resolveFoodMediaUrl(u)} alt="" className="w-20 h-20 object-cover rounded-lg border" />
                  <button
                    type="button"
                    className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow border"
                    onClick={() => removePhoto(u)}
                  >
                    <Trash2 size={12} className="text-red-500" />
                  </button>
                </div>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 text-xs font-medium text-indigo-600 cursor-pointer">
              <Upload size={14} /> {uploading ? 'Subiendo…' : 'Subir foto'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadPhoto(f);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-5">
          <button
            type="button"
            className="btn-secondary text-sm inline-flex items-center gap-1"
            onClick={() => { void exportPdf(); }}
          >
            <FileDown size={14} /> PDF
          </button>
          <button type="button" className="btn-secondary text-sm flex-1" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn-primary text-sm flex-1" disabled={saving} onClick={save}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
